/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Check, Loader2, X } from 'lucide-react';
import { DiscographyId, useDiscographyIds } from '../useDiscographyIds';
import { useCallback, useMemo, useState } from 'react';
import { useApi } from 'shared_components/context';
import { useClientId } from 'shared_components/context/client';
import { generateArtistUrlBySource } from './utils';
import { usePrimaryDiscographyRun } from '../usePrimaryDiscographyRun';

export function SourceIdResolver({
  artist,
  source,
  source_id,
  onChange,
}: {
  artist: DiscographyId;
  source: keyof DiscographyId['source_ids'];
  source_id: string;
  onChange?: (props: {
    valid: boolean;
    approved: boolean;
    rejected: boolean;
  }) => void;
}) {
  const { clientId } = useClientId();
  const { adminApi: api } = useApi();
  const { getCollectedDiscographyIds } = useDiscographyIds();
  const [loadingValidate, setLoadingValidate] = useState(false);
  const [loadingReject, setLoadingReject] = useState(false);

  const { actionsDisabled } = usePrimaryDiscographyRun();

  const sourceId = useMemo(() => {
    return artist.source_ids[source].find((s) => s.id === source_id);
  }, [artist, source, source_id]);

  const validateSourceId = useCallback(() => {
    if (!clientId || !sourceId || actionsDisabled) return;

    setLoadingValidate(true);

    const sourceIdUpdate = {
      valid: sourceId.valid,
      approved: true,
      rejected: false,
    };
    const shared = {
      userId: clientId,
      sourceId: source_id,
      artistSourceIdUpdate: sourceIdUpdate,
    };

    (artist.type === 'group'
      ? api.updateBandSourceIdSourceId({
          bandId: artist.id,
          ...shared,
        })
      : api.updateAliasSourceIdSourceId({
          aliasId: artist.id,
          ...shared,
        })
    )
      .then(() => {
        getCollectedDiscographyIds(clientId);
        onChange?.({
          valid: sourceId.valid,
          approved: true,
          rejected: false,
        });
      })
      .catch((ex) => {
        console.log(ex);
      })
      .finally(() => {
        setLoadingValidate(false);
      });
  }, [api, source_id, artist.id, sourceId, actionsDisabled]);

  const rejectSourceId = useCallback(() => {
    if (!clientId || !sourceId || actionsDisabled) return;

    setLoadingReject(true);

    const shared = {
      userId: clientId,
      sourceId: source_id,
      artistSourceIdUpdate: {
        valid: sourceId.valid,
        approved: false,
        rejected: true,
      },
    };

    (artist.type === 'group'
      ? api.updateBandSourceIdSourceId({
          bandId: artist.id,
          ...shared,
        })
      : api.updateAliasSourceIdSourceId({
          aliasId: artist.id,
          ...shared,
        })
    )
      .then(() => {
        getCollectedDiscographyIds(clientId);
        onChange?.({
          valid: sourceId.valid,
          approved: false,
          rejected: true,
        });
      })
      .catch((ex) => {
        console.log(ex);
      })
      .finally(() => {
        setLoadingReject(false);
      });
  }, [api, source_id, artist.id, sourceId, actionsDisabled]);

  const uncertainSourceId = useCallback(() => {
    if (!clientId || !sourceId || actionsDisabled) return;

    setLoadingReject(true);

    const shared = {
      userId: clientId,
      sourceId: source_id,
      artistSourceIdUpdate: {
        valid: false,
        approved: false,
        rejected: false,
      },
    };

    (artist.type === 'group'
      ? api.updateBandSourceIdSourceId({
          bandId: artist.id,
          ...shared,
        })
      : api.updateAliasSourceIdSourceId({
          aliasId: artist.id,
          ...shared,
        })
    )
      .then(() => {
        getCollectedDiscographyIds(clientId);
        onChange?.({
          valid: false,
          approved: false,
          rejected: false,
        });
      })
      .catch((ex) => {
        console.log(ex);
      })
      .finally(() => {
        setLoadingReject(false);
      });
  }, [api, source_id, artist.id, sourceId, actionsDisabled]);

  if (!sourceId) return null;

  const isApproved = sourceId.approved;
  const isRejected = sourceId.rejected;
  const isValid = sourceId.valid && !isRejected && !isApproved;
  const isUnderReview = !isValid && !isRejected && !isApproved;

  return (
    <div className="tw-group tw-flex tw-items-center tw-justify-center tw-relative tw-w-full">
      <div className="tw-flex tw-flex-col tw-w-full">
        <div className="tw-flex tw-gap-1">
          <div>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={generateArtistUrlBySource(sourceId.id, source)}
              className="tw-text-gray-400 tw-mr-1 tw-underline"
            >
              {sourceId.original_name}
            </a>
          </div>
          {!actionsDisabled ? (
            <div className="tw-flex tw-justify-end tw-items-start tw-flex-grow">
              <div className="tw-flex tw-gap-1 tw-items-center tw-justify-center">
                <button
                  className={[
                    'tw-p-0.5  tw-rounded tw-cursor-pointer',
                    isApproved || isValid
                      ? 'tw-bg-westburyGreen'
                      : 'tw-bg-gray-500',
                  ].join(' ')}
                  title="Mark as valid"
                  type="button"
                  onClick={validateSourceId}
                >
                  {loadingValidate ? (
                    <Loader2 size={10} className="tw-animate-spin" />
                  ) : (
                    <Check size={10} strokeWidth="4" />
                  )}
                </button>
                <button
                  onClick={uncertainSourceId}
                  type="button"
                  className={[
                    'tw-p-o tw-m-0 tw-rounded tw-cursor-pointer',
                    isUnderReview ? 'tw-bg-westburyAmber' : 'tw-bg-gray-500',
                  ].join(' ')}
                  title="Uncertain"
                >
                  {loadingReject ? (
                    <Loader2 size={10} className="tw-animate-spin" />
                  ) : (
                    <div
                      className="tw-text-xs tw-flex tw-items-center tw-justify-center tw-p-0 tw-m-0"
                      style={{
                        height: '14px',
                        width: '14px',
                        lineHeight: '12px',
                        fontSize: '12px',
                      }}
                    >
                      ?
                    </div>
                  )}
                </button>
                <button
                  onClick={rejectSourceId}
                  type="button"
                  className={[
                    'tw-p-0.5 tw-rounded tw-cursor-pointer',
                    isRejected ? 'tw-bg-westburyRed' : 'tw-bg-gray-500',
                  ].join(' ')}
                  title="Reject"
                >
                  {loadingReject ? (
                    <Loader2 size={10} className="tw-animate-spin" />
                  ) : (
                    <X size={10} strokeWidth="4" />
                  )}
                </button>
              </div>
            </div>
          ) : null}
        </div>
        <span
          title={sourceId.id}
          className={[
            'tw-text-ellipsis tw-overflow-hidden',
            isApproved ? 'tw-text-westburyGreen' : '',
            isRejected ? 'tw-text-westburyRed' : '',
            isValid ? 'tw-text-westburyBlue' : '',
            isUnderReview ? 'tw-text-westburyAmber' : '',
          ].join(' ')}
        >
          {sourceId.id}
        </span>
      </div>
    </div>
  );
}
